import React from 'react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import AppLayout from './layouts/AppLayout';
import { ChatProvider } from './contexts/ChatContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Invite from './pages/Invite';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import RecordTypeManager from './pages/admin/RecordTypeManager';
import FieldManager from './pages/admin/FieldManager';
import ScreenManager from './pages/admin/ScreenManager';
import ScreenBuilder from './pages/admin/ScreenBuilder';
import WorkflowBuilder from './pages/admin/WorkflowBuilder';
import Settings from './pages/admin/Settings';
import TemplateStore from './pages/admin/TemplateStore';
import RecycleBin from './pages/admin/RecycleBin';
import TitleBuilder from './pages/admin/TitleBuilder';
import RelationshipManager from './pages/admin/RelationshipManager';
import OrphanedDataReport from './pages/admin/OrphanedDataReport';
import RolesManager from './pages/admin/RolesManager';
import TeamsManager from './pages/admin/TeamsManager';
import StagingManager from './pages/admin/StagingManager';
import UserManager from './pages/admin/UserManager';
import TemplateLibrary from './pages/admin/TemplateLibrary';
import SOPManager from './pages/admin/SOPManager';
import AIBillingDashboard from './pages/admin/AIBillingDashboard';
import AIQualityDashboard from './pages/admin/AIQualityDashboard';
import RecordActivityLog from './pages/RecordActivityLog';
import ActivityLog from './pages/ActivityLog';
import GlobalNotes from './pages/GlobalNotes';
import DiaryPage from './pages/DiaryPage';
import DocumentsAudit from './pages/DocumentsAudit';
import DiaryTypesManager from './pages/admin/DiaryTypesManager';
import EmailSettings from './pages/admin/EmailSettings';
import PartyRolesManager from './pages/admin/PartyRolesManager';
import PhoneSettings from './pages/admin/PhoneSettings';
import TemplateEditor from './pages/admin/TemplateEditor';
import Profile from './pages/Profile';
import Portal from './pages/Portal';
import RecordList from './pages/RecordList';
import RecordCreate from './pages/RecordCreate';
import IntakeCreate from './pages/intake/IntakeCreate';
import RecordDetail from './pages/RecordDetail';
import MyTasks from './pages/MyTasks';

function ProtectedRoute({ children }) {
  const { token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { token, user } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return children;
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<Login />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/register" element={<Register />} />
      <Route path="/invite" element={<Invite />} />

      <Route path="/" element={
        <ProtectedRoute><ChatProvider><AppLayout /></ChatProvider></ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />

        <Route path="records/:typeSlug" element={<RecordList />} />
        <Route path="records/intake/new" element={<IntakeCreate />} />
        <Route path="records/:typeSlug/new" element={<RecordCreate />} />
        <Route path="records/:typeSlug/:recordId" element={<RecordDetail />} />
        <Route path="records/:typeSlug/:recordId/activity" element={<RecordActivityLog />} />
        <Route path="activity-log" element={<ActivityLog />} />
        <Route path="notes" element={<GlobalNotes />} />
        <Route path="diary/:slug" element={<DiaryPage />} />
        <Route path="documents-audit" element={<DocumentsAudit />} />
        <Route path="tasks" element={<MyTasks />} />

        <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="admin/record-types" element={<AdminRoute><RecordTypeManager /></AdminRoute>} />
        <Route path="admin/templates" element={<AdminRoute><TemplateStore /></AdminRoute>} />
        <Route path="admin/recycle-bin" element={<AdminRoute><RecycleBin /></AdminRoute>} />
        <Route path="admin/data-report" element={<AdminRoute><OrphanedDataReport /></AdminRoute>} />
        <Route path="admin/record-types/:recordTypeId/fields" element={<AdminRoute><FieldManager /></AdminRoute>} />
        <Route path="admin/record-types/:recordTypeId/screens" element={<AdminRoute><ScreenManager /></AdminRoute>} />
        <Route path="admin/record-types/:recordTypeId/screens/:screenId/builder" element={<AdminRoute><ScreenBuilder /></AdminRoute>} />
        <Route path="admin/record-types/:recordTypeId/workflow" element={<AdminRoute><WorkflowBuilder /></AdminRoute>} />
        <Route path="admin/record-types/:recordTypeId/title" element={<AdminRoute><TitleBuilder /></AdminRoute>} />
        <Route path="admin/record-types/:recordTypeId/relationships" element={<AdminRoute><RelationshipManager /></AdminRoute>} />
        <Route path="admin/roles" element={<AdminRoute><RolesManager /></AdminRoute>} />
        <Route path="admin/teams" element={<AdminRoute><TeamsManager /></AdminRoute>} />
        {/* Staging route removed — super admin only, use admin-dev.sixty10.com */}
        <Route path="admin/users" element={<AdminRoute><UserManager /></AdminRoute>} />
        <Route path="admin/diary-types" element={<AdminRoute><DiaryTypesManager /></AdminRoute>} />
        <Route path="admin/email-settings" element={<AdminRoute><EmailSettings /></AdminRoute>} />
        <Route path="admin/party-roles" element={<AdminRoute><PartyRolesManager /></AdminRoute>} />
        <Route path="admin/phone-settings" element={<AdminRoute><PhoneSettings /></AdminRoute>} />
        <Route path="admin/knowledge-base" element={<AdminRoute><SOPManager /></AdminRoute>} />
        <Route path="admin/ai-billing" element={<AdminRoute><AIBillingDashboard /></AdminRoute>} />
        <Route path="admin/ai-quality" element={<AdminRoute><AIQualityDashboard /></AdminRoute>} />
        <Route path="admin/doc-templates" element={<AdminRoute><TemplateLibrary /></AdminRoute>} />
        <Route path="admin/doc-templates/new" element={<AdminRoute><TemplateEditor /></AdminRoute>} />
        <Route path="admin/doc-templates/:id/edit" element={<AdminRoute><TemplateEditor /></AdminRoute>} />
        <Route path="admin/settings" element={<AdminRoute><Settings /></AdminRoute>} />

        <Route path="profile" element={<Profile />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </>
  ),
  { future: { v7_relativeSplatPath: true } }
);

function App() {
  return (
    <>
      <Toaster position="top-right" />
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </>
  );
}

export default App;
